import { useMemo, useState } from "react";
import { inzetBySector } from "../lib/data";
import { useFirestoreCollection } from "../hooks/useFirestoreCollection";
import { addIncident, setChecklistItem, getMelderString } from "../lib/firestore-ops";
import { useNow } from "../hooks/useNow";
import { hhmmToMinutes, minutesNow, isCurrent } from "../lib/time";
import { QuickLog } from "./QuickLog";
import { Icon } from "./Icon";
import type { ChecklistEntry, InzetItem } from "../lib/types";

const SECTOR_LABELS: Record<string, string> = {
  STAFF: "Staf & supervisors",
  TOEGANG: "Toegangscontrole",
  POSTEN: "Statische posten",
  ROULATIE: "Roulatie / onvoorzien",
  HGL: "Hondengeleider",
  MOBILITEIT: "Mobiliteit / verkeer (BVN)",
  SERVICE: "Service",
  IBT: "IBT-team",
  OVERIG: "Overig",
};

function posKey(item: InzetItem): string {
  return `pos:${item.id}`;
}

function sectorLocation(sector: string | null, item: InzetItem): string {
  // Use beschrijving as primary location for incident pre-fill
  const sectorLabel = sector ? SECTOR_LABELS[sector] ?? sector : "";
  return item.beschrijving
    ? sectorLabel
      ? `${sectorLabel} · ${item.beschrijving}`
      : item.beschrijving
    : sectorLabel || "Overig";
}

export function Inzet() {
  const [open, setOpen] = useState<string | null>("STAFF");
  // Tijdsactivering: posities kleuren mee met hun dienstvenster (60s tick)
  const now = useNow(60_000);
  const nowMin = minutesNow(now);
  const [composeFor, setComposeFor] = useState<string | null>(null);
  const sectors = Object.entries(inzetBySector).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const { data: checklist } = useFirestoreCollection<ChecklistEntry>(
    "checklist",
  );
  const checkMap = useMemo(
    () => new Map(checklist.map((c) => [c.id, c])),
    [checklist],
  );

  return (
    <ul className="space-y-1.5">
      {sectors.map(([sector, items]) => {
        const total = items.reduce((s, i) => s + (Number(i.aantal) || 0), 0);
        const checkedCount = items.filter(
          (i) => checkMap.get(posKey(i))?.checked,
        ).length;
        const isOpen = open === sector;
        const allChecked = checkedCount === items.length && items.length > 0;

        return (
          <li key={sector} className="card overflow-hidden">
            <button
              onClick={() => setOpen(isOpen ? null : sector)}
              className="w-full flex items-center gap-3 px-3 py-2.5 active:bg-ink-700 hover:bg-ink-700/40 transition-colors"
            >
              <span className="text-sm font-medium text-zinc-100 flex-1 text-left">
                {SECTOR_LABELS[sector] ?? sector}
              </span>
              <span
                className={`text-2xs tabular pill ${
                  allChecked
                    ? "bg-ok/15 text-ok"
                    : checkedCount > 0
                      ? "bg-warn/15 text-warn"
                      : "bg-ink-800 text-zinc-500"
                }`}
                aria-label="Op positie aangevinkt"
              >
                {checkedCount}/{items.length} op positie
              </span>
              <span className="text-2xs text-zinc-500 tabular hidden sm:inline">
                {total} pers
              </span>
              <span className="text-zinc-600 text-xs">{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && (
              <ul className="border-t border-line divide-y divide-line/60">
                {items.map((i) => {
                  const k = posKey(i);
                  const checked = checkMap.get(k)?.checked ?? false;
                  const composing = composeFor === k;
                  return (
                    <li key={i.id} className="px-2 py-1">
                      <div className="flex items-stretch gap-1">
                        <button
                          onClick={() =>
                            setChecklistItem(k, !checked, getMelderString())
                          }
                          aria-label={
                            checked
                              ? `Markeer ${i.beschrijving} als niet op positie`
                              : `Markeer ${i.beschrijving} als op positie`
                          }
                          aria-pressed={checked}
                          className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                            checked
                              ? "text-ok"
                              : "text-zinc-600 hover:text-zinc-300 active:bg-ink-700"
                          }`}
                        >
                          <span
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                              checked
                                ? "bg-ok border-ok text-ink-950"
                                : "border-line"
                            }`}
                          >
                            {checked && <Icon.Check className="w-3.5 h-3.5" />}
                          </span>
                        </button>
                        <button
                          onClick={() =>
                            setChecklistItem(k, !checked, getMelderString())
                          }
                          className="flex-1 min-w-0 text-left py-2 pr-1 active:bg-ink-700/40 rounded no-scale"
                        >
                          <div className="flex items-baseline gap-2">
                            {(() => {
                              const vanMin = hhmmToMinutes(i.van);
                              const totMin = hhmmToMinutes(i.tot);
                              const actief = isCurrent(vanMin, totMin, nowMin);
                              const voorbij =
                                totMin != null && nowMin >= totMin;
                              return (
                                <span
                                  aria-label={
                                    actief
                                      ? "dienstvenster actief"
                                      : voorbij
                                        ? "dienst afgelopen"
                                        : "dienst nog niet begonnen"
                                  }
                                  className={`shrink-0 w-2 h-2 rounded-full self-center ${
                                    actief
                                      ? "bg-ok animate-pulse"
                                      : voorbij
                                        ? "bg-zinc-700"
                                        : "bg-zinc-500"
                                  }`}
                                />
                              );
                            })()}
                            <span className="text-2xs tabular text-zinc-400 font-mono shrink-0">
                              {i.van}–{i.tot}
                            </span>
                            <span
                              className={`text-sm truncate ${
                                checked ? "text-zinc-500" : "text-zinc-200"
                              }`}
                            >
                              {i.beschrijving}
                            </span>
                          </div>
                          <div className="text-2xs text-zinc-500 mt-0.5">
                            {i.type}
                            {i.aantal ? ` × ${i.aantal}` : ""}
                            {i.positie ? ` · pos ${i.positie}` : ""}
                          </div>
                        </button>
                        <button
                          onClick={() =>
                            setComposeFor(composing ? null : k)
                          }
                          aria-label={`Maak melding voor positie ${i.beschrijving}`}
                          title="Meld iets bij deze positie"
                          aria-expanded={composing}
                          className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                            composing
                              ? "bg-accent/20 text-accent"
                              : "text-zinc-500 hover:text-accent active:bg-ink-700"
                          }`}
                        >
                          <Icon.Flag className="w-4 h-4" />
                        </button>
                      </div>
                      {composing && (
                        <div className="mt-2 ml-12">
                          <QuickLog
                            compact
                            prefill={{
                              location: sectorLocation(sector, i),
                              severity: "let-op",
                            }}
                            onCancel={() => setComposeFor(null)}
                            onSubmit={async (incident) => {
                              await addIncident(incident);
                              setComposeFor(null);
                            }}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
